import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { productsAPI, categoriesAPI } from '../../services/api';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [stockFilter, setStockFilter] = useState('all'); // all, low, out

  useEffect(() => {
    loadData();
  }, [selectedCategory]);

  const loadData = async () => {
    try {
      const [productsData, categoriesData] = await Promise.all([
        productsAPI.getAll({ category: selectedCategory || undefined }),
        categoriesAPI.getAll()
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
    
    try {
      await productsAPI.delete(id);
      loadData();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const filteredProducts = products.filter(product => {
    // Search filter
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Stock filter
    let matchesStock = true;
    if (stockFilter === 'out') {
      matchesStock = product.stock === 0;
    } else if (stockFilter === 'low') {
      matchesStock = product.stock > 0 && product.stock <= 5;
    }
    
    return matchesSearch && matchesStock;
  });

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'N/A';
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Produits</h1>
          <div className="flex gap-3">
            <Link
              to="/admin/products/new"
              className="flex items-center gap-2 bg-cyan-500 text-white px-4 py-2 rounded-lg hover:bg-cyan-600 transition"
            >
              <Plus className="w-5 h-5" />
              Nouveau Produit
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-blue-800 border border-blue-700 rounded-lg pl-12 pr-4 py-3 text-white placeholder-gray-400"
            />
          </div>
          <select
            value={stockFilter}
            onChange={(e) => setStockFilter(e.target.value)}
            className="bg-blue-800 border border-blue-700 rounded-lg px-4 py-3 text-white"
          >
            <option value="all">Tous les stocks</option>
            <option value="out">🔴 Rupture de stock</option>
            <option value="low">⚠️ Stock faible (≤5)</option>
          </select>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-blue-800 border border-blue-700 rounded-lg px-4 py-3 text-white"
          >
            <option value="">Toutes les catégories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Products Count */}
        <div className="mb-4 text-gray-400 text-sm">
          {filteredProducts.length} produit(s) trouvé(s)
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-400 border-t-transparent"></div>
          </div>
        ) : (
          <div className="bg-blue-800 rounded-lg border border-blue-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-blue-900">
                <tr>
                  <th className="text-left px-6 py-4 text-gray-300 font-semibold">Produit</th>
                  <th className="text-left px-6 py-4 text-gray-300 font-semibold">Catégorie</th>
                  <th className="text-left px-6 py-4 text-gray-300 font-semibold">Prix</th>
                  <th className="text-left px-6 py-4 text-gray-300 font-semibold">Stock</th>
                  <th className="text-right px-6 py-4 text-gray-300 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-700">
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-blue-700/30 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <img
                          src={product.image_url || 'https://via.placeholder.com/60'}
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                        <div>
                          <div className="text-white font-medium">{product.name}</div>
                          {product.featured && (
                            <span className="text-xs text-yellow-400">⭐ Vedette</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-400">{getCategoryName(product.category_id)}</td>
                    <td className="px-6 py-4 text-white font-semibold">
                      {product.price.toLocaleString('fr-DZ')} DA
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        product.stock === 0
                          ? 'bg-red-500/20 text-red-400'
                          : product.stock <= 5
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {product.stock}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/admin/products/edit/${product.id}`}
                          className="p-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredProducts.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                Aucun produit trouvé
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Products;
